/**
 * Jackpot System Types
 *
 * TypeScript interfaces for the jackpot feature including wheels,
 * tickets, spins, and progress tracking.
 */

// ============================================================================
// WHEEL SEGMENT TYPES
// ============================================================================

/**
 * Color scheme for wheel segments
 */
export type SegmentColor = 'blue' | 'green' | 'purple' | 'orange' | 'red' | 'gold';

/**
 * Main wheel segment configuration
 */
export interface WheelSegment {
  id: number;
  prize: string;
  color: SegmentColor;
  tokens: number;
  isJackpot?: boolean;
}

/**
 * Jackpot wheel percentage segment
 */
export interface JackpotWheelSegment {
  id: number;
  percentage: number;
  color: SegmentColor;
}

// ============================================================================
// POOL & TICKETS
// ============================================================================

/**
 * Current jackpot pool information
 * Aligned with backend GET /api/v1/jackpot/pool response
 */
export interface JackpotPool {
  pool_amount: number;
  total_contributed: number;
  total_distributed: number;
  last_big_win_at: string | null;
  last_big_win_user_id: string | null;
  last_big_win_amount: number | null;
}

/**
 * User's jackpot tickets
 * Aligned with backend GET /api/v1/jackpot/tickets response
 */
export interface JackpotTickets {
  total_tickets: number;
  available_tickets: number;
  used_tickets: number;
  tickets: TicketDetail[];
  entitlements: EntitlementProgress[];
}

/**
 * Individual ticket details
 * Aligned with backend response
 */
export interface TicketDetail {
  ticket_id: string;
  entitlement_id: string | null;
  source: 'entitlement' | 'purchase' | 'auto_grant';
  ticket_number: number;
  is_used: boolean;
  used_at: string | null;
  spin_id: string | null;
  created_at: string;
  expires_at: string;
  is_expired: boolean;
}

// ============================================================================
// SPIN RESULTS
// ============================================================================

/**
 * Main wheel spin result
 * Aligned with backend response
 */
export interface MainWheelResult {
  slot_index: number;
  prize_type: string;
  tokens_won: number;
  triggers_jackpot: boolean;
}

/**
 * Jackpot wheel spin result (only when jackpot hit)
 * Aligned with backend response
 */
export interface JackpotWheelResult {
  slot_index: number;
  prize_type: string;
  percentage: number;
  pool_at_spin: number;
  tokens_won: number;
  /** Degrees the wheel should spin for the animation */
  animation_degrees: number;
}

/**
 * Complete spin result
 * Aligned with backend POST /api/v1/jackpot/spin response
 */
export interface SpinResult {
  success: boolean;
  spin_id: string;
  ticket_id: string;
  main_wheel: MainWheelResult;
  jackpot_wheel: JackpotWheelResult | null;
  total_tokens_won: number;
  new_token_balance: number;
  remaining_tickets: number;
}

/**
 * Result from spinning all tickets at once
 * Aligned with backend POST /api/v1/jackpot/spin-all response
 */
export interface SpinAllResult {
  success: boolean;
  total_spins: number;
  total_tokens_won: number;
  jackpot_hits: number;
  spins: SpinResult[];
  new_token_balance: number;
}

// ============================================================================
// TICKET ENTITLEMENTS & PROGRESS
// ============================================================================

/**
 * Ticket tier type
 */
export type TicketTier = 'basic' | 'pro' | 'enterprise' | 'free_bonus';

/**
 * Entitlement status
 */
export type EntitlementStatus = 'active' | 'completed' | 'expired';

/**
 * Entitlement progress from backend
 * Aligned with backend EntitlementProgress model
 */
export interface EntitlementProgress {
  entitlement_id: string;
  tier: TicketTier;
  package_id: string | null;
  tickets_claimable: number;
  tickets_claimed: number;
  tickets_remaining: number;
  status: EntitlementStatus;
  expires_at: string | null;
  tickets_earned_this_checkin: number;
  // Tier-specific fields (optional based on tier)
  current_streak?: number;
  streak_required?: number;
  weeks_checked_in?: number;
  days_logged_in?: number;
}

// ============================================================================
// CHECKIN & LOGIN REWARDS
// ============================================================================

/**
 * Response from daily check-in
 * Aligned with backend POST /api/v1/jackpot/checkin response
 */
export interface CheckinResponse {
  success: boolean;
  message: string;
  tickets_earned: number;
  entitlements: EntitlementProgress[];
  free_bonus_progress: EntitlementProgress;
  total_available_tickets: number;
}

/**
 * Response from ticket purchase
 * Aligned with backend POST /api/v1/jackpot/tickets/purchase response
 */
export interface TicketPurchaseResponse {
  success: boolean;
  tickets_purchased: number;
  tokens_spent: number;
  new_token_balance: number;
  total_available_tickets: number;
}

// ============================================================================
// TICKET TIER CONFIGURATION
// ============================================================================

/**
 * Ticket tier configuration
 */
export interface TicketTierConfig {
  tokens: number;
  tickets: number;
  condition: string;
  tier: TicketTier | null;
}

/**
 * Ticket tiers mapping (for UI display)
 * These match the backend JACKPOT_PACKAGE_TIER_MAP
 */
export const TICKET_TIERS: Record<number, TicketTierConfig> = {
  5000: { tokens: 5000, tickets: 0, condition: '', tier: null },
  25000: { tokens: 25000, tickets: 1, condition: '7 consecutive daily logins', tier: 'basic' },
  100000: { tokens: 100000, tickets: 4, condition: '1 login per week for 4 weeks', tier: 'pro' },
  1000000: {
    tokens: 1000000,
    tickets: 60,
    condition: '2 tickets/day for 30 days',
    tier: 'enterprise',
  },
};

/**
 * Ticket price in tokens (for direct purchase)
 */
export const JACKPOT_TICKET_PRICE_TOKENS = 500;

// ============================================================================
// WHEEL CONFIGURATION
// ============================================================================

/**
 * Main wheel segment color mapping
 */
export const SEGMENT_COLORS: Record<number, SegmentColor> = {
  5: 'blue',
  10: 'green',
  25: 'purple',
  50: 'orange',
  100: 'red',
};

/**
 * Segment color hex values
 */
export const SEGMENT_COLOR_HEX: Record<SegmentColor, string> = {
  blue: '#60A5FA',
  green: '#34D399',
  purple: '#A78BFA',
  orange: '#FB923C',
  red: '#F87171',
  gold: 'linear-gradient(135deg, #FFD700, #FFA500)',
};

/**
 * Main wheel segments (48 total)
 */
export const MAIN_WHEEL_SEGMENTS: WheelSegment[] = [
  // Jackpot slot (1)
  { id: 0, prize: 'JACKPOT', color: 'gold', tokens: 0, isJackpot: true },
  // Regular slots (47)
  // Distribution: 15x 5, 12x 10, 10x 25, 6x 50, 4x 100
  { id: 1, prize: '5', color: 'blue', tokens: 5 },
  { id: 2, prize: '10', color: 'green', tokens: 10 },
  { id: 3, prize: '5', color: 'blue', tokens: 5 },
  { id: 4, prize: '25', color: 'purple', tokens: 25 },
  { id: 5, prize: '5', color: 'blue', tokens: 5 },
  { id: 6, prize: '10', color: 'green', tokens: 10 },
  { id: 7, prize: '50', color: 'orange', tokens: 50 },
  { id: 8, prize: '5', color: 'blue', tokens: 5 },
  { id: 9, prize: '10', color: 'green', tokens: 10 },
  { id: 10, prize: '25', color: 'purple', tokens: 25 },
  { id: 11, prize: '5', color: 'blue', tokens: 5 },
  { id: 12, prize: '100', color: 'red', tokens: 100 },
  { id: 13, prize: '10', color: 'green', tokens: 10 },
  { id: 14, prize: '5', color: 'blue', tokens: 5 },
  { id: 15, prize: '25', color: 'purple', tokens: 25 },
  { id: 16, prize: '10', color: 'green', tokens: 10 },
  { id: 17, prize: '50', color: 'orange', tokens: 50 },
  { id: 18, prize: '5', color: 'blue', tokens: 5 },
  { id: 19, prize: '10', color: 'green', tokens: 10 },
  { id: 20, prize: '5', color: 'blue', tokens: 5 },
  { id: 21, prize: '25', color: 'purple', tokens: 25 },
  { id: 22, prize: '5', color: 'blue', tokens: 5 },
  { id: 23, prize: '100', color: 'red', tokens: 100 },
  { id: 24, prize: '10', color: 'green', tokens: 10 },
  { id: 25, prize: '50', color: 'orange', tokens: 50 },
  { id: 26, prize: '5', color: 'blue', tokens: 5 },
  { id: 27, prize: '25', color: 'purple', tokens: 25 },
  { id: 28, prize: '10', color: 'green', tokens: 10 },
  { id: 29, prize: '5', color: 'blue', tokens: 5 },
  { id: 30, prize: '50', color: 'orange', tokens: 50 },
  { id: 31, prize: '10', color: 'green', tokens: 10 },
  { id: 32, prize: '25', color: 'purple', tokens: 25 },
  { id: 33, prize: '5', color: 'blue', tokens: 5 },
  { id: 34, prize: '100', color: 'red', tokens: 100 },
  { id: 35, prize: '10', color: 'green', tokens: 10 },
  { id: 36, prize: '5', color: 'blue', tokens: 5 },
  { id: 37, prize: '25', color: 'purple', tokens: 25 },
  { id: 38, prize: '50', color: 'orange', tokens: 50 },
  { id: 39, prize: '10', color: 'green', tokens: 10 },
  { id: 40, prize: '5', color: 'blue', tokens: 5 },
  { id: 41, prize: '25', color: 'purple', tokens: 25 },
  { id: 42, prize: '10', color: 'green', tokens: 10 },
  { id: 43, prize: '5', color: 'blue', tokens: 5 },
  { id: 44, prize: '100', color: 'red', tokens: 100 },
  { id: 45, prize: '50', color: 'orange', tokens: 50 },
  { id: 46, prize: '25', color: 'purple', tokens: 25 },
  { id: 47, prize: '10', color: 'green', tokens: 10 },
];

/**
 * Jackpot wheel segments (24 total)
 * Distribution: weighted towards lower percentages
 */
export const JACKPOT_WHEEL_SEGMENTS: JackpotWheelSegment[] = [
  { id: 0, percentage: 1, color: 'blue' },
  { id: 1, percentage: 2, color: 'blue' },
  { id: 2, percentage: 3, color: 'green' },
  { id: 3, percentage: 1, color: 'blue' },
  { id: 4, percentage: 4, color: 'green' },
  { id: 5, percentage: 2, color: 'blue' },
  { id: 6, percentage: 5, color: 'purple' },
  { id: 7, percentage: 1, color: 'blue' },
  { id: 8, percentage: 3, color: 'green' },
  { id: 9, percentage: 15, color: 'orange' }, // Silver
  { id: 10, percentage: 2, color: 'blue' },
  { id: 11, percentage: 4, color: 'green' },
  { id: 12, percentage: 1, color: 'blue' },
  { id: 13, percentage: 25, color: 'gold' }, // Gold
  { id: 14, percentage: 3, color: 'green' },
  { id: 15, percentage: 5, color: 'purple' },
  { id: 16, percentage: 2, color: 'blue' },
  { id: 17, percentage: 1, color: 'blue' },
  { id: 18, percentage: 4, color: 'green' },
  { id: 19, percentage: 15, color: 'orange' }, // Silver
  { id: 20, percentage: 3, color: 'green' },
  { id: 21, percentage: 2, color: 'blue' },
  { id: 22, percentage: 5, color: 'purple' },
  { id: 23, percentage: 1, color: 'blue' },
];
